import { useEffect, useState, useCallback } from 'react'
import { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { Tables } from '../types/supabase'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/useAuthContext'

type Message = Tables<'messages'>
type MessageAttachment = Tables<'message_attachments'>

export interface MessageWithAttachments extends Message {
  attachments: MessageAttachment[]
  sender_info?: {
    name: string | null
    role: string | null
  }
}

interface RealtimeMessagesOptions {
  reportId: string
  onNewMessage?: (message: Message) => void
  onMessageRead?: (messageId: string) => void
  enabled?: boolean
}

// Hook for realtime message subscriptions
export function useRealtimeMessages({ 
  reportId, 
  onNewMessage,
  onMessageRead,
  enabled = true
}: RealtimeMessagesOptions) {
  const [channel, setChannel] = useState<RealtimeChannel | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!reportId || !enabled) return

    // Create a channel for this report's messages
    const messagesChannel = supabase
      .channel(`messages:report_id=eq.${reportId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `report_id=eq.${reportId}`
        },
        (payload) => {
          const newMessage = payload.new as Message
          
          // Update query cache
          queryClient.invalidateQueries({ queryKey: ['messages', reportId] })
          queryClient.invalidateQueries({ queryKey: ['report', reportId] })
          
          // Call callback if provided
          onNewMessage?.(newMessage)
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `report_id=eq.${reportId}`
        },
        (payload) => {
          const updatedMessage = payload.new as Message
          const oldMessage = payload.old as Message
          
          // Check if message was marked as read
          if (updatedMessage.read_at && !oldMessage.read_at) {
            onMessageRead?.(updatedMessage.id)
          }
          
          // Update query cache
          queryClient.invalidateQueries({ queryKey: ['messages', reportId] })
        }
      )
      .subscribe((status) => {
        setIsConnected(status === 'SUBSCRIBED')
      })

    setChannel(messagesChannel)

    // Cleanup on unmount
    return () => {
      messagesChannel.unsubscribe()
    }
  }, [reportId, onNewMessage, onMessageRead, queryClient, enabled])

  return {
    channel,
    isConnected
  }
}

// Enhanced hook with realtime messages
export function useMessagesWithRealtime(reportId: string) {
  const [messages, setMessages] = useState<MessageWithAttachments[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // Fetch messages using the new RPC function
  const fetchMessages = useCallback(async () => {
    if (!reportId) return

    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase
        .rpc('get_messages_with_status', { p_report_id: reportId })

      if (error) throw error

      // Transform the data to match our type
      const transformedMessages: MessageWithAttachments[] = (data || []).map((msg: Message & { attachments?: MessageAttachment[] }) => ({
        id: msg.id,
        report_id: msg.report_id,
        content: msg.content,
        is_admin_message: msg.is_admin_message,
        sender_name: msg.sender_name,
        sender_role: msg.sender_role,
        created_at: msg.created_at,
        read_at: msg.read_at,
        metadata: msg.metadata,
        updated_at: msg.updated_at,
        attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
        sender_info: {
          name: msg.sender_name,
          role: msg.sender_role
        }
      }))

      setMessages(transformedMessages)
    } catch (err) {
      console.error('Error fetching messages:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch messages')
    } finally {
      setLoading(false)
    }
  }, [reportId])

  // Set up realtime subscription
  useRealtimeMessages({
    reportId,
    onNewMessage: () => {
      // Refetch messages when a new one arrives
      fetchMessages()
    },
    enabled: !!reportId
  })

  // Initial fetch
  useEffect(() => {
    if (reportId) {
      fetchMessages()
    }
  }, [reportId, fetchMessages])

  // Send a new message
  const sendMessage = async (
    content: string, 
    options?: {
      isAdmin?: boolean
      senderName?: string
      senderRole?: string
      metadata?: Record<string, unknown>
    }
  ) => {
    try {
      const { data, error } = await supabase.rpc('send_message', {
        p_report_id: reportId,
        p_content: content,
        p_is_admin: options?.isAdmin || false,
        p_sender_name: options?.senderName || (options?.isAdmin && user ? user.email : null),
        p_sender_role: options?.senderRole || (options?.isAdmin ? 'admin' : 'reporter'),
        p_metadata: options?.metadata || {}
      })

      if (error) throw error

      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['messages', reportId] })
      queryClient.invalidateQueries({ queryKey: ['report', reportId] })

      return { success: true, messageId: data }
    } catch (err) {
      console.error('Error sending message:', err)
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Failed to send message' 
      }
    }
  }

  // Mark a message as read
  const markAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase.rpc('mark_message_read', {
        p_message_id: messageId
      })

      if (error) throw error

      // Update local state optimistically
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, read_at: new Date().toISOString() }
          : msg
      ))

      return { success: true }
    } catch (err) {
      console.error('Error marking message as read:', err)
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Failed to mark message as read' 
      }
    }
  }

  // Get unread count
  const unreadCount = messages.filter(m => !m.read_at && m.is_admin_message).length

  return {
    messages,
    loading,
    error,
    sendMessage,
    markAsRead,
    refetch: fetchMessages,
    unreadCount
  }
}